<template>
  <div v-rect-loading="loading" class="question-editor">
    <div class="header">
      <div class="title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedFrom
        ref="formEditor"
        :labelWidth="labelWidth"
        :model="question"
        :formLines="formLines"
      >
        <template #type="{ lineName, line }">
          <el-select v-model="question[lineName]" :disabled="isEditMode" :placeholder="line.placeholder" @change="handleTypeChange">
            <el-option
              v-for="(item, key) in questionType"
              :key="key"
              :label="$t(item.i18nKey)"
              :value="item.value"
            />
          </el-select>
        </template>
        <template #options="{ lineName, line }">
          <div class="option-list-container">
            <draggable
              v-model="question[lineName]"
              tag="el-collapse"
              handle=".drag-handler"
              animation="200"
              group="description"
              ghostClass="ghost"
              class="option-list"
              :component-data="collapseConfig">
              <el-collapse-item
                v-for="(item, index) in question[lineName]"
                class="option-item"
                :key="index"
                :name="item._vid">
                <template #title>
                  <div class="collapse-title-wrapper">
                    <div class="option-item-title">
                      <div class="item-name">{{ $t(`${i18nPrefix}.label.optionNo`, { number: index + 1 }) }}</div>
                      <div class="item-preview">
                        <span>{{ item.content_cn || '-' }}</span>
                        <span>{{ item.content_tc || '-' }}</span>
                        <span>{{ item.content_en || '-' }}</span>
                      </div>
                    </div>
                    <div class="option-operate" @click.stop="() => {}">
                      <div class="drag-handler">
                        <i class="iconfont iconMore"></i>
                      </div>
                      <div class="add-btn">
                        <i class="el-icon-circle-plus-outline" @click="addOptionItem(index)"></i>
                      </div>
                      <div v-if="question[lineName].length > 1" class="remove-btn">
                        <i class="el-icon-remove-outline" @click="removeOptionItem(index)"></i>
                      </div>
                    </div>
                  </div>
                </template>
                <WrappedFrom
                  class="option-item-form"
                  ref="optionItemForm"
                  :labelWidth="optionNameLabelWidth"
                  :model="question[lineName][index]"
                  :formLines="line.subLine"
                >
                </WrappedFrom>
              </el-collapse-item>
            </draggable>
          </div>
        </template>
        <template #prefill_source="{ lineName, line }">
          <el-select v-model="question[lineName]" clearable :placeholder="line.placeholder">
            <el-option
              v-for="item in prefillSourceOptions"
              :key="item.value || 'none'"
              :label="$t(item.i18nKey)"
              :value="item.value"
            />
          </el-select>
        </template>
        <template #link_config>
          <div class="structured-config" :style="structuredConfigStyle">
            <div class="config-block">
              <div class="link-config-line">
                <div class="link-config-line-label">{{ $t(`${i18nPrefix}.label.agreementLink.cn`) }}</div>
                <el-input v-model="configForm.link_url_cn" :placeholder="$t(`${i18nPrefix}.placeholder.linkUrl`)" class="link-config-line-control" />
              </div>
              <div class="link-config-line">
                <div class="link-config-line-label">{{ $t(`${i18nPrefix}.label.agreementLink.tc`) }}</div>
                <el-input v-model="configForm.link_url_tc" :placeholder="$t(`${i18nPrefix}.placeholder.linkUrl`)" class="link-config-line-control" />
              </div>
              <div class="link-config-line">
                <div class="link-config-line-label">{{ $t(`${i18nPrefix}.label.agreementLink.en`) }}</div>
                <el-input v-model="configForm.link_url_en" :placeholder="$t(`${i18nPrefix}.placeholder.linkUrl`)" class="link-config-line-control" />
              </div>
              <div class="link-config-line link-config-line-textarea">
                <div class="link-config-line-label is-required">{{ $t(`${i18nPrefix}.label.agreementText.cn`) }}</div>
                <el-input
                  v-model="configForm.link_text_cn"
                  type="textarea"
                  :autosize="{ minRows: 4, maxRows: 10 }"
                  :placeholder="$t(`${i18nPrefix}.placeholder.linkText`)"
                  class="link-config-line-control link-config-textarea"
                />
              </div>
              <div class="link-config-line link-config-line-textarea">
                <div class="link-config-line-label is-required">{{ $t(`${i18nPrefix}.label.agreementText.tc`) }}</div>
                <el-input
                  v-model="configForm.link_text_tc"
                  type="textarea"
                  :autosize="{ minRows: 4, maxRows: 10 }"
                  :placeholder="$t(`${i18nPrefix}.placeholder.linkText`)"
                  class="link-config-line-control link-config-textarea"
                />
              </div>
              <div class="link-config-line link-config-line-textarea">
                <div class="link-config-line-label is-required">{{ $t(`${i18nPrefix}.label.agreementText.en`) }}</div>
                <el-input
                  v-model="configForm.link_text_en"
                  type="textarea"
                  :autosize="{ minRows: 4, maxRows: 10 }"
                  :placeholder="$t(`${i18nPrefix}.placeholder.linkText`)"
                  class="link-config-line-control link-config-textarea"
                />
              </div>
            </div>
          </div>
        </template>
        <template #config_text>
          <div class="structured-config" :style="structuredConfigStyle">
            <div v-if="isDateRangeType" class="config-block">
              <div class="config-line">
                <div class="config-label">{{ $t(`${i18nPrefix}.label.dateFormat`) }}</div>
                <el-select v-model="configForm.format" class="config-control">
                  <el-option
                    v-for="item in dateFormatOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </div>
            </div>
            <div v-if="isRepeatGroupType" class="config-block">
              <div class="config-row">
                <div class="config-item">
                  <div class="config-label">{{ $t(`${i18nPrefix}.label.minRows`) }}</div>
                  <el-input-number v-model="configForm.min_rows" :min="0" :controls="false" class="config-control" />
                </div>
                <div class="config-item">
                  <div class="config-label">{{ $t(`${i18nPrefix}.label.maxRows`) }}</div>
                  <el-input-number v-model="configForm.max_rows" :min="0" :controls="false" class="config-control" />
                </div>
              </div>
              <div class="config-row">
                <div class="config-item">
                  <div class="config-label">{{ $t(`${i18nPrefix}.label.addButtonText.cn`) }}</div>
                  <el-input v-model="configForm.add_button_text_cn" class="config-control" />
                </div>
                <div class="config-item">
                  <div class="config-label">{{ $t(`${i18nPrefix}.label.addButtonText.tc`) }}</div>
                  <el-input v-model="configForm.add_button_text_tc" class="config-control" />
                </div>
                <div class="config-item">
                  <div class="config-label">{{ $t(`${i18nPrefix}.label.addButtonText.en`) }}</div>
                  <el-input v-model="configForm.add_button_text_en" class="config-control" />
                </div>
              </div>
              <div class="sub-field-header">
                <span>{{ $t(`${i18nPrefix}.label.subFields`) }}</span>
              </div>
              <div
                v-for="(item, index) in configForm.sub_fields"
                :key="index"
                class="sub-field-item"
              >
                <div class="sub-field-title">
                  <span>{{ isEn() ? item.label_en : item.label_cn || $t(`${i18nPrefix}.label.subFieldNo`, { number: index + 1 }) }}</span>
                  <el-button type="text" @click="removeSubField(index)">
                    {{ $t('common.btn.delete') }}
                  </el-button>
                </div>
                <div class="sub-field-grid">
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.subFieldKey`) }}</div>
                    <el-input v-model="item.key" :placeholder="$t(`${i18nPrefix}.placeholder.subFieldKey`)" class="config-control" />
                  </div>
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.subFieldType`) }}</div>
                    <el-select v-model="item.type" class="config-control">
                      <el-option
                        v-for="typeItem in subFieldTypeOptions"
                        :key="typeItem.value"
                        :label="$t(typeItem.i18nKey)"
                        :value="typeItem.value"
                      />
                    </el-select>
                  </div>
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.required`) }}</div>
                    <el-checkbox v-model="item.required">
                      {{ $t(`${i18nPrefix}.label.required`) }}
                    </el-checkbox>
                  </div>
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.width`) }}</div>
                    <el-input v-model="item.width" :placeholder="$t(`${i18nPrefix}.placeholder.width`)" class="config-control" />
                  </div>
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.subFieldLabel.cn`) }}</div>
                    <el-input v-model="item.label_cn" :placeholder="$t(`${i18nPrefix}.placeholder.subFieldLabel`)" class="config-control" />
                  </div>
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.subFieldLabel.tc`) }}</div>
                    <el-input v-model="item.label_tc" :placeholder="$t(`${i18nPrefix}.placeholder.subFieldLabel`)" class="config-control" />
                  </div>
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.subFieldLabel.en`) }}</div>
                    <el-input v-model="item.label_en" :placeholder="$t(`${i18nPrefix}.placeholder.subFieldLabel`)" class="config-control" />
                  </div>
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.subFieldPlaceholder.cn`) }}</div>
                    <el-input v-model="item.placeholder_cn" :placeholder="$t(`${i18nPrefix}.placeholder.subFieldPlaceholder`)" class="config-control" />
                  </div>
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.subFieldPlaceholder.tc`) }}</div>
                    <el-input v-model="item.placeholder_tc" :placeholder="$t(`${i18nPrefix}.placeholder.subFieldPlaceholder`)" class="config-control" />
                  </div>
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.subFieldPlaceholder.en`) }}</div>
                    <el-input v-model="item.placeholder_en" :placeholder="$t(`${i18nPrefix}.placeholder.subFieldPlaceholder`)" class="config-control" />
                  </div>
                </div>
                <div v-if="item.type === 'DATE_RANGE'" class="sub-field-type-config">
                  <div class="config-item">
                    <div class="config-label">{{ $t(`${i18nPrefix}.label.dateFormat`) }}</div>
                    <el-select v-model="item.format" class="config-control">
                      <el-option
                        v-for="fItem in dateFormatOptions"
                        :key="fItem.value"
                        :label="fItem.label"
                        :value="fItem.value"
                      />
                    </el-select>
                  </div>
                </div>
              </div>
              <div class="sub-field-footer">
                <el-button size="mini" icon="el-icon-plus" @click="addSubField">
                  {{ $t('common.btn.add') }}
                </el-button>
              </div>
            </div>
          </div>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { JOB_APPLICATION_QUESTION_TYPE, QUESTION_SCOPED_TYPE, QUESTION_UNSUPPORT_TYPE, QUESTION_STRUCTURED_TYPE, QUESTION_PREFILL_SOURCE } from '@/constant/questionnaire'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import draggable from 'vuedraggable'
import { isEn } from '@/i18n'

export default {
  components: {
    WrappedFrom,
    draggable
  },
  data () {
    return {
      isEn,
      loading: false,
      allQuestionType: JOB_APPLICATION_QUESTION_TYPE,
      scopedType: QUESTION_SCOPED_TYPE,
      unSupportType: QUESTION_UNSUPPORT_TYPE,
      structuredType: QUESTION_STRUCTURED_TYPE,
      prefillSource: QUESTION_PREFILL_SOURCE,
      i18nPrefix: 'admin.company.jobApplication',
      questionType: {},
      question: {
        options: [],
        type: null,
        prefill_source: null,
        config_text: ''
      },
      configForm: {},
      placeholderHidden: false,
      optionHidden: true,
      activeOptionItems: [],
      optionsVid: 1
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    isScopedType () {
      return (this.scopedType.includes(this.question.type) && this.unSupportType.includes(this.question.type)) || this.question.type === this.allQuestionType.LOCATION.value
    },
    isStructuredType () {
      return this.structuredType.includes(this.question.type)
    },
    isDateRangeType () {
      return this.question.type === this.allQuestionType.DATE_RANGE.value
    },
    isRepeatGroupType () {
      return this.question.type === this.allQuestionType.REPEAT_GROUP.value
    },
    isLinkType () {
      return this.question.type === this.allQuestionType.LINK.value
    },
    prefillSourceOptions () {
      return _.values(this.prefillSource)
    },
    dateFormatOptions () {
      return [
        { label: 'YYYY-MM', value: 'YYYY-MM' }
      ]
    },
    subFieldTypeOptions () {
      return [
        { i18nKey: `${this.i18nPrefix}.label.subFieldTypeText`, value: 'TEXT' },
        { i18nKey: `${this.i18nPrefix}.label.subFieldTypeDateRange`, value: 'DATE_RANGE' }
      ]
    },
    formLines () {
      const contentRule = { required: true, message: this.$t(`${this.i18nPrefix}.message.questionNameRequired`) }
      const optionsRule = { required: true, message: this.$t(`${this.i18nPrefix}.message.optionNameRequired`), trigger: 'change' }
      const contentLines = {}
      const placeholderLines = {}
      const localeSequence = ['cn', 'tc', 'en']
      if (this.isScopedType) {
        _.forEach(this.scopedType, typeItem => {
          const typeName = typeItem.toLowerCase()
          _.forEach(localeSequence, item => {
            contentLines[`${typeName}_content_${item}`] = {
              label: this.$t(`${this.i18nPrefix}.label.name.${typeName}.${item}`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionName`),
              rules: contentRule
            }
            placeholderLines[`${typeName}_placeholder_${item}`] = {
              label: this.$t(`${this.i18nPrefix}.label.placeholder.${typeName}.${item}`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.placeholder`),
              hidden: this.placeholderHidden
            }
          })
        })
      } else {
        _.forEach(localeSequence, item => {
          contentLines[`content_${item}`] = {
            label: this.$t(`${this.i18nPrefix}.label.name.${item}`),
            placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionName`),
            rules: contentRule
          }
          placeholderLines[`placeholder_${item}`] = {
            label: this.$t(`${this.i18nPrefix}.label.placeholder.${item}`),
            placeholder: this.$t(`${this.i18nPrefix}.placeholder.placeholder`),
            hidden: this.placeholderHidden
          }
        })
      }

      const lines = {
        type: {
          label: this.$t(`${this.i18nPrefix}.label.questionType`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionType`),
          contentSlot: 'type',
          rules: { required: true, message: this.$t(`${this.i18nPrefix}.message.questionTypeRequired`) }
        },
        ...contentLines,
        ...placeholderLines,
        prefill_source: {
          label: this.$t(`${this.i18nPrefix}.label.prefillSource`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.prefillSource`),
          contentSlot: 'prefill_source'
        },
        config_text: {
          label: this.$t(`${this.i18nPrefix}.label.questionConfig`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionConfig`),
          hidden: !this.isStructuredType && !this.isLinkType,
          lineSlot: this.isLinkType ? 'link_config' : '',
          contentSlot: 'config_text'
        },
        options: {
          label: this.$t(`${this.i18nPrefix}.label.name.cn`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.optionName`),
          hidden: this.optionHidden,
          contentSlot: 'options',
          rules: optionsRule,
          subLine: {
            content_cn: {
              label: this.$t(`${this.i18nPrefix}.label.name.cn`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.optionName`),
              rules: optionsRule
            },
            content_tc: {
              label: this.$t(`${this.i18nPrefix}.label.name.tc`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.optionName`),
              rules: optionsRule
            },
            content_en: {
              label: this.$t(`${this.i18nPrefix}.label.name.en`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.optionName`),
              rules: optionsRule
            }

          }
        }
      }
      return lines
    },
    collapseConfig () {
      const config = {
        props: {
          value: this.activeOptionItems
        },
        on: {
          change: this.handleCollapseChange
        }
      }
      return config
    },
    form () {
      return this.$refs.formEditor
    },
    labelWidth () {
      if (this.isScopedType) {
        if (isEn()) {
          return '330px'
        } else {
          return '240px'
        }
      }
      if (isEn()) {
        return '270px'
      } else {
        return '142px'
      }
    },
    structuredConfigStyle () {
      return {
        '--link-config-label-width': this.labelWidth
      }
    },
    optionNameLabelWidth () {
      if (isEn()) {
        return '210px'
      } else {
        return '120px'
      }
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}.title.question.${this.isEditMode ? 'edit' : 'add'}`)
    }
  },
  created () {
    // now is not support 'LOCATION' type question
    this.questionType = _.filter(this.allQuestionType, (value, key) => (!this.scopedType.includes(key) && !this.unSupportType.includes(key)))
    if (this.isEditMode) {
      this.getQuestionData()
    }
  },
  methods: {
    ...mapActions('jobApplicationManagement', [
      'getQuestionById',
      'createQuestion',
      'updateQuestion'
    ]),
    async getQuestionData () {
      this.loading = true
      try {
        const questionId = this.$route.params.questionId
        const resp = await this.getQuestionById(questionId)
        if (resp) {
          const question = resp.question
          if (!question.options) {
            question.options = []
          }
          if (_.isString(question.config)) {
            question.config_text = question.config
          } else if (question.config) {
            question.config_text = JSON.stringify(question.config, null, 2)
          }
          _.forEach(question.options, item => {
            item._vid = ++this.optionsVid
          })
          if (question.type === this.allQuestionType.LOCATION.value) {
            _.forEach(this.scopedType, item => {
              const typeName = item.toLowerCase()
              const questionItem = question[typeName]
              _.forEach(questionItem, (value, key) => {
                question[`${typeName}_${key}`] = value
              })
            })
          }
          this.question = question
          this.handleTypeChange(question.type)
        }
      } finally {
        this.loading = false
      }
    },
    handleTypeChange (type) {
      if (type === this.allQuestionType.LINK.value) {
        this.placeholderHidden = true
      } else {
        this.placeholderHidden = false
      }
      if (type === this.allQuestionType.SINGLE.value || type === this.allQuestionType.MULTIPLE.value) {
        this.optionHidden = false
        if (this.question.options && this.question.options.length === 0) {
          this.question.options = []
          this.addOptionItem(0)
        }
      } else {
        this.optionHidden = true
      }
      if ((this.structuredType.includes(type) || type === this.allQuestionType.LINK.value) && !this.question.config_text) {
        this.configForm = this.getDefaultStructuredConfig(type)
        this.syncConfigText()
      } else if (this.structuredType.includes(type) || type === this.allQuestionType.LINK.value) {
        this.configForm = this.normalizeStructuredConfig(type, this.getConfigFromQuestion())
        this.syncConfigText()
      } else {
        this.configForm = {}
      }
      this.$nextTick(() => this.form.clearValidate())
    },
    handleCollapseChange (activeNames) {
      this.activeOptionItems = activeNames
    },
    addOptionItem (index) {
      this.optionsVid += 1
      this.question.options.splice(index + 1, 0, {
        _vid: this.optionsVid,
        content_en: null,
        content_tc: null,
        content_cn: null
      })
      this.activeOptionItems.push(this.optionsVid)
    },
    removeOptionItem (index) {
      this.question.options.splice(index, 1)
    },
    handleCancel () {
      this.$router.push('/v-admin/job-application-management/question/list')
    },
    async handleSave () {
      let result = false
      result = await this.form.submit()
      if (this.question.type === this.allQuestionType.SINGLE.value || this.question.type === this.allQuestionType.MULTIPLE.value) {
        const optionResults = await Promise.all(this.$refs.optionItemForm.map((form) => form.submit()))
        result = _.every(optionResults, (item) => item) && result
      }
      if (!result) {
        return
      }
      this.loading = true
      try {
        let question = {
          content_cn: this.question.content_cn,
          content_tc: this.question.content_tc,
          content_en: this.question.content_en,
          type: this.question.type,
          placeholder_cn: this.question.placeholder_cn,
          placeholder_tc: this.question.placeholder_tc,
          placeholder_en: this.question.placeholder_en,
          prefill_source: this.question.prefill_source || null
        }
        if (this.structuredType.includes(question.type) || question.type === this.allQuestionType.LINK.value) {
          const config = this.parseStructuredConfig()
          if (!config) {
            return
          }
          question.config = config
          question.options = []
        }
        if (question.type === this.allQuestionType.LINK.value) {
          question.options = []
        } else if (question.type === this.allQuestionType.LOCATION.value) {
          const scopedQuestion = {}
          _.forEach(this.question, (value, key) => {
            _.forEach(this.scopedType, item => {
              const typeName = item.toLowerCase()
              if (key.startsWith(`${typeName}_`)) {
                const questionKey = key.replace(`${typeName}_`, '')
                const questionItem = scopedQuestion[typeName] || {}
                questionItem[questionKey] = value
                scopedQuestion[typeName] = questionItem
              }
            })
          })
          question = {
            type: this.question.type,
            ...scopedQuestion
          }
        } else {
          question.options = _.map(this.question.options, (item, index) => {
            return {
              id: item.id,
              content_cn: item.content_cn,
              content_tc: item.content_tc,
              content_en: item.content_en,
              order: index + 1
            }
          })
        }
        let saveFunction = null
        if (this.isEditMode) {
          question.id = this.question.id
          saveFunction = this.updateQuestion
        } else {
          saveFunction = this.createQuestion
        }
        const resp = await saveFunction(question)
        if (resp) {
          this.$router.push('/v-admin/job-application-management/question/list')
        }
      } finally {
        this.loading = false
      }
    },
    parseStructuredConfig () {
      const config = this.normalizeStructuredConfig(this.question.type, this.configForm)
      if (this.question.type === this.allQuestionType.REPEAT_GROUP.value) {
        if (config.max_rows < config.min_rows) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.maxRowsInvalid`))
          return null
        }
        const invalidSubField = _.find(config.sub_fields, item => !item.key || !item.type || !item.label_cn || !item.label_tc || !item.label_en)
        if (invalidSubField) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.subFieldRequired`))
          return null
        }
        const unsupportedSubField = _.find(config.sub_fields, item => item.type === 'SINGLE')
        if (unsupportedSubField) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.subFieldTypeUnsupported`))
          return null
        }
      }
      if (this.question.type === this.allQuestionType.LINK.value) {
        if (!_.trim(config.link_text_cn) || !_.trim(config.link_text_tc) || !_.trim(config.link_text_en)) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.agreementTextRequired`))
          return null
        }
      }
      this.configForm = config
      this.syncConfigText()
      return config
    },
    getConfigFromQuestion () {
      if (_.isString(this.question.config)) {
        try {
          return JSON.parse(this.question.config)
        } catch (e) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.questionConfigInvalid`))
          return {}
        }
      }
      if (_.isObject(this.question.config)) {
        return this.question.config
      }
      if (!this.question.config_text) {
        return {}
      }
      try {
        return JSON.parse(this.question.config_text)
      } catch (e) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.questionConfigInvalid`))
        return {}
      }
    },
    normalizeStructuredConfig (type, config) {
      const defaultConfig = this.getDefaultStructuredConfig(type)
      if (!config || !_.isObject(config)) {
        return defaultConfig
      }
      if (type === this.allQuestionType.REPEAT_GROUP.value) {
        const subFields = _.map(config.sub_fields || defaultConfig.sub_fields, item => {
          const subField = {
            key: '',
            type: 'TEXT',
            label_cn: '',
            label_tc: '',
            label_en: '',
            placeholder_cn: '',
            placeholder_tc: '',
            placeholder_en: '',
            required: true,
            width: '',
            ...item
          }
          delete subField.allow_present
          return subField
        })
        return {
          ...defaultConfig,
          ...config,
          min_rows: _.toNumber(_.isNil(config.min_rows) ? defaultConfig.min_rows : config.min_rows),
          max_rows: _.toNumber(_.isNil(config.max_rows) ? defaultConfig.max_rows : config.max_rows),
          sub_fields: subFields
        }
      }
      if (type === this.allQuestionType.LINK.value) {
        return {
          link_text_cn: config.link_text_cn || '',
          link_text_tc: config.link_text_tc || '',
          link_text_en: config.link_text_en || '',
          link_url_cn: config.link_url_cn || '',
          link_url_tc: config.link_url_tc || '',
          link_url_en: config.link_url_en || ''
        }
      }
      if (type === this.allQuestionType.DATE_RANGE.value) {
        return {
          format: config.format || defaultConfig.format
        }
      }
      return {
        ...defaultConfig,
        ...config
      }
    },
    getDefaultStructuredConfig (type) {
      if (type === this.allQuestionType.DATE_RANGE.value) {
        return {
          format: 'YYYY-MM'
        }
      }
      if (type === this.allQuestionType.LINK.value) {
        return {
          link_text_cn: '',
          link_text_tc: '',
          link_text_en: '',
          link_url_cn: '',
          link_url_tc: '',
          link_url_en: ''
        }
      }
      if (type === this.allQuestionType.REPEAT_GROUP.value) {
        return {
          min_rows: 0,
          max_rows: 10,
          add_button_text_cn: this.$t('questionnaire.btn.addEntry', 'zh'),
          add_button_text_tc: this.$t('questionnaire.btn.addEntry', 'hk'),
          add_button_text_en: this.$t('questionnaire.btn.addEntry', 'en'),
          sub_fields: []
        }
      }
      return {}
    },
    syncConfigText () {
      this.$set(this.question, 'config_text', JSON.stringify(this.configForm || {}, null, 2))
    },
    addSubField () {
      if (!this.configForm.sub_fields) {
        this.$set(this.configForm, 'sub_fields', [])
      }
      this.configForm.sub_fields.push(this.getTextSubField('', '', '', '', '180px'))
    },
    removeSubField (index) {
      this.configForm.sub_fields.splice(index, 1)
    },
    getTextSubField (key, labelCn, labelTc, labelEn, width) {
      return {
        key: key,
        type: 'TEXT',
        label_cn: labelCn,
        label_tc: labelTc,
        label_en: labelEn,
        placeholder_cn: '',
        placeholder_tc: '',
        placeholder_en: '',
        required: true,
        width: width,
        format: 'YYYY-MM'
      }
    }
  },
  watch: {

  }
}
</script>

<style lang="scss" scoped>
.question-editor {
  .header {
    border-bottom: 1px solid #e4e7ed;

    .title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .option-list-container {
    .option-list {
      min-height: 20px;

      .option-item {
        width: 100%;
        padding-right: 50px;
        box-sizing: content-box;
        position: relative;

        &.ghost {
          opacity: 0.5;

          ::v-deep {
            .el-collapse-item__wrap, .el-collapse-item__header {
              background-color: #c8ebfb;
            }
          }
        }

        ::v-deep .el-collapse-item__content {
          padding-bottom: 6px;
        }

        .collapse-title-wrapper {
          width: calc(100% - 20px);
          height: 100%;

          .option-item-title {
            display: flex;
            width: 100%;
            overflow: hidden;

            .item-name {
              margin-right: 10px;
            }

            .item-preview {
              flex: 1;
              text-align: center;
              overflow: hidden;
              word-break: keep-all;
              white-space: nowrap;
              text-overflow: ellipsis;

              span {
                height: 25px;
                line-height: 25px;
                padding: 0 5px;

                &:not(:last-child) {
                  border-right: 1px solid #ccc;
                }
              }
            }
          }

          .option-operate {
            display: flex;
            position: absolute;
            top: 2px;
            left: calc(100% - 50px);

            > div {
              margin-right: 5px;

              &:hover {
                color: #0096cc;

                i {
                  font-size: 22px;
                }
              }
            }

            i {
              font-size: 18px;
            }

            .drag-handler {
              display: none;
              cursor: grab;
            }
          }
        }

        .option-item-form {
          width: 100%;
          ::v-deep {
            .el-form-item {
              margin-bottom: 22px;
            }
          }
        }
      }
    }
  }

  .structured-config {
    .config-block {
      width: 100%;
    }

    .link-config-line {
      display: flex;
      align-items: flex-start;
      margin-bottom: 18px;
    }

    .link-config-line-textarea {
      margin-bottom: 22px;
    }

    .link-config-line-label {
      width: var(--link-config-label-width);
      padding-right: 14px;
      box-sizing: border-box;
      line-height: 36px;
      text-align: right;
      font-size: 14px;
      color: #606266;
      white-space: nowrap;

      &.is-required::after {
        content: '*';
        margin-left: 4px;
        color: #f56c6c;
      }
    }

    .link-config-line-control {
      flex: 1;
      min-width: 0;
    }

    .link-config-textarea {
      ::v-deep textarea {
        line-height: 22px;
      }
    }

    .config-line {
      display: flex;
      align-items: center;
      margin-top: 12px;
    }

    .config-row {
      display: flex;
      gap: 12px;
      margin-bottom: 12px;
      align-items: end;
    }

    .config-item {
      flex: 1;
      min-width: 0;
      margin-bottom: 12px;
    }

    .config-item-full {
      width: 100%;
    }

    .config-label {
      margin-bottom: 6px;
      line-height: 20px;
      font-size: 13px;
      color: #606266;
    }

    .config-control {
      width: 100%;
    }

    .sub-field-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 4px 0 10px;
      line-height: 28px;
      font-size: 14px;
      color: #303133;
    }

    .sub-field-item {
      margin-bottom: 12px;
      padding: 12px;
      border: 1px solid #e4e7ed;
      border-radius: 2px;
      background: #fafafa;
    }

    .sub-field-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
      line-height: 24px;
      font-weight: 500;
      color: #303133;
    }

    .sub-field-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 12px;
    }
  }

  .form-container {
    width: 760px;
    margin: 30px auto 0;

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
      }

      .save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
